import { graphHooks } from '@tunasong/graph-lib/react'
import { dayjs } from '@tunasong/models'
import type { ExportWorkflowOutput } from '@tunasong/redux'
import { useIsLocalHost, workflowsApi } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import {
  Divider,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { EntitySelectDialog } from '../entity/entity-select-dialog.js'
import { LocalLibraryDownload } from './local-library-download.js'

export interface JobsProps {}

/** How long to wait between status checks while a run is in flight. */
const pollMs = 5000

/**
 * Step Functions statuses, said the way someone waiting for a download would say them.
 * The raw enum leaks the state machine; "SUCCEEDED" is not what a person calls a file
 * that is ready to download (Nielsen: match between system and the real world).
 */
const statusLabels: Record<ExportWorkflowOutput['status'], string> = {
  SUCCEEDED: 'Ready',
  RUNNING: 'Preparing…',
  PENDING_REDRIVE: 'Retrying',
  FAILED: 'Failed',
  TIMED_OUT: 'Timed out',
  ABORTED: 'Cancelled',
}

/** Terminal states that produced no zip. The only useful thing to offer here is another go. */
const isUnfinished = (status: ExportWorkflowOutput['status']) =>
  status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED'

interface ExportRunProps {
  run: ExportWorkflowOutput
  /** Start the same export again. Disabled while anything else is in flight. */
  retryDisabled: boolean
  onRetry(run: ExportWorkflowOutput): void
}

/**
 * One past or in-flight run. What the user is scanning for is *which* export this is, so the
 * scope leads and the status follows it — an account export has no root; a space export names
 * the space (spec §4) by name while the user can still read it, by id once they cannot.
 */
const ExportRun: FC<ExportRunProps> = ({ run, retryDisabled, onRetry }) => {
  const { entity } = graphHooks.useEntity(run.rootEntityId)
  const scope = run.rootEntityId ? (entity?.name ?? run.rootEntityId) : 'All my data'
  /**
   * Identify a run by when it *started*: a run still in flight has no stop time, and the API
   * fills that gap with "now", so stopDate would relabel a running export on every poll.
   */
  const started = dayjs(run.startDate).format('D MMM YYYY, HH:mm')
  const unfinished = isUnfinished(run.status)

  return (
    <ListItem>
      <ListItemText
        primary={scope}
        secondary={
          unfinished
            ? `${statusLabels[run.status]} · ${started} · Nothing was changed — an export only reads.`
            : `${statusLabels[run.status]} · ${started}`
        }
      />
      <ListItemSecondaryAction>
        {unfinished ? (
          <Button variant="outlined" color="primary" disabled={retryDisabled} onClick={() => onRetry(run)}>
            Try again
          </Button>
        ) : (
          <Button
            href={run.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            color="primary"
            /** Every row's button reads "Download"; the name has to say which run it downloads. */
            aria-label={`Download ${scope}, ${started}`}
            disabled={run.status !== 'SUCCEEDED' || !run.url}
          >
            Download
          </Button>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  )
}

/** The cloud's export workflow: start an export, watch it, download the zip */
const CloudExports: FC = () => {
  /**
   * A run only changes state server-side, so the page has to ask. Poll while something is in
   * flight and stop once it settles — the list endpoint describes and signs every execution,
   * so an idle page must not pay for it, and a background tab must not either.
   */
  const [pollingInterval, setPollingInterval] = useState(0)
  const {
    currentData: exports = [],
    isLoading,
    isFetching,
  } = workflowsApi.useGetExportsQuery(undefined, { pollingInterval, skipPollingIfUnfocused: true })
  const [doExport, exportStatus] = workflowsApi.useExportMutation()
  const [pickingSpace, setPickingSpace] = useState(false)

  const isRunning = exports[0]?.status === 'RUNNING'
  useEffect(() => setPollingInterval(isRunning ? pollMs : 0), [isRunning])

  /**
   * One run at a time. `isFetching` covers the gap between the mutation succeeding and the
   * refetch it invalidates showing the new run — we must NOT hold this on the mutation's own
   * `isSuccess`, which never resets and would disable both triggers for the rest of the session.
   */
  const disabled = exportStatus.isLoading || isFetching || isRunning

  /** The trigger's promise is not awaited here: success and failure both reach the page through `exportStatus` */
  const handleExportAll = () => {
    void doExport()
  }

  const handleSpacePicked = (selected?: Persisted<Entity>) => {
    setPickingSpace(false)
    if (selected) {
      void doExport({ rootEntityId: selected.id })
    }
  }

  const handleRetry = (run: ExportWorkflowOutput) => {
    void doExport(run.rootEntityId ? { rootEntityId: run.rootEntityId } : undefined)
  }

  if (isLoading) {
    return <LinearProgress />
  }
  return (
    <div>
      <Button variant="contained" color="primary" onClick={handleExportAll} disabled={disabled}>
        Export my data
      </Button>{' '}
      <Button variant="outlined" color="primary" onClick={() => setPickingSpace(true)} disabled={disabled}>
        Export a space…
      </Button>
      <EntitySelectDialog
        open={pickingSpace}
        title="Export a space"
        description="Exports everything inside it — songs, chords, audio and images — as a zip a local Tunasong opens directly. Pick a bandspace, a folder, or any single item to start from."
        confirmLabel="Export"
        onClose={handleSpacePicked}
      />
      {isRunning ? (
        <Typography variant="body2">
          Preparing your export. This takes a few minutes — you can leave this page and come back; it appears below when
          it is ready.
        </Typography>
      ) : null}
      <Divider>Exports</Divider>
      {exports.length === 0 ? (
        <Typography variant="body2">
          No exports yet. Export a space to open it in a local Tunasong, or export your data for a full copy of your
          account.
        </Typography>
      ) : (
        <List>
          {exports.map(e => (
            <ExportRun key={e.name} run={e} retryDisabled={disabled} onRetry={handleRetry} />
          ))}
        </List>
      )}
    </div>
  )
}

export const Exports: FC<JobsProps> = props => {
  const {} = props
  const isLocalHost = useIsLocalHost()
  return isLocalHost ? <LocalLibraryDownload /> : <CloudExports />
}

export default Exports
